/**
 * this component displays fulfillment types used for the order placed
 *
 * @module views/components/OrderConfirmationDetails
 * @memberof -Common
 */
import './OrderConfirmationDetails.scss';

import React, { useMemo, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Accordion from '@ulta/core/components/Accordion/Accordion';
import Icon from '@ulta/core/components/Icon/Icon';
import Text from '@ulta/core/components/Text/Text';
import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { hasItems } from '@ulta/core/utils/array/array';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import OrderConfirmationItems from '@ulta/components/OrderConfirmationItems/OrderConfirmationItems';

import { descendingHtmlTag } from '@ulta/utils/accessibility/accessibility';
import { BOPIS_MAP_SIZE_SPLIT_CART, GOOGLE_STATIC_MAPS_URL, MAP_ZOOM_LEVEL_KEY, MARKER_COLOR } from '@ulta/utils/constants/googleMaps';

import Markdown from '../Markdown/Markdown';
import Tag from '../Tag/Tag';
import UltaAsset from '../UltaAsset/UltaAsset';
import * as utils from './OrderConfirmationDetails';

/**
 * Represents a OrderConfirmationDetails component
 *
 * @method
 * @param { OrderConfirmationDetailsProps} props - React properties passed from composition
 * @returns OrderConfirmationDetails
 */
export const OrderConfirmationDetails = function( props ){
  const {
    icon,
    title,
    subtitle,
    expandAction,
    collapseAction,
    deliveryInformation,
    instructions,
    instructionTitle,
    orderId,
    shipmentTitle,
    shipmentDetail,
    giftItems,
    giftItemsTitle,
    giftItemsSubtitle,
    items,
    itemsTitle,
    itemsSubtitle,
    pickupStoreInformation,
    signatureInformation,
    listPriceAccessibilityLabel,
    salePriceAccessibilityLabel,
    trustedPartnersLabel,
    trustedPartnersIcon
  } = props;
  const { googleMapsApiKey } = useAppConfigContext();
  const { breakpoint } = useDeviceInflection();
  const latitude = pickupStoreInformation?.storeInfo?.lat;
  const longitude = pickupStoreInformation?.storeInfo?.long;
  const showMap = !!( latitude && longitude );

  const tierLabelHtmlTag = descendingHtmlTag( title?.htmlTag );
  const [expandMore, setExpandMore] = useState( false );

  const mapUrl = useMemo( () => utils.getMapUrl( {
    latitude: pickupStoreInformation?.storeInfo?.lat,
    longitude: pickupStoreInformation?.storeInfo?.long,
    breakpoint,
    key: googleMapsApiKey
  } ), [pickupStoreInformation?.storeInfo?.lat, pickupStoreInformation?.storeInfo?.long, breakpoint, googleMapsApiKey] );


  const hasProductItems = hasItems( items );
  const hasGifts = hasItems( giftItems );
  const showAvailabilityTag = !!pickupStoreInformation?.availabilityTag;
  const showAvailabilityTagAsATag = pickupStoreInformation?.availabilityTag?.variant === 'utilityCompact';
  const showAvailabilityTagAsText = !showAvailabilityTagAsATag;
  const showInstructions = !!( pickupStoreInformation?.helpText || instructions || showMap );

  const storeAddress = `${pickupStoreInformation?.storeInfo?.address1}, ${ pickupStoreInformation?.storeInfo?.address2 }, ${ pickupStoreInformation?.storeInfo?.address3 }`;

  const headerChildren = trustedPartnersLabel ?
    <div className='headerWrapper'>
      <div className='iconSection'>
        <Icon
          name={ icon }
          size='xxl'
        />
      </div>
      <div className='textSection'>
        <div className='topSection'>
          <div className='titleSection'>
            <Text
              textStyle='title-6'
              ariaHidden={ true }
              htmlTag={ title?.htmlTag }
            >
              { title?.text }
              <span className='sr-only'>{ trustedPartnersLabel }</span>
            </Text>
          </div>
          <div className='trustedPartnerSection'>
            <Icon
              name={ trustedPartnersIcon }
            />
            <Text
              htmlTag='span'
              textStyle='body-sm'
              ariaHidden={ true }
            >
              { trustedPartnersLabel }
            </Text>
          </div>
        </div>
        <div className='bottomSection'>
          <Text
            textStyle='body-sm'
            ariaHidden={ true }
            color='neutral-500'
          >
            { subtitle }
          </Text>
        </div>
      </div>
    </div> :
    null;

  return (
    <div className='OrderConfirmationDetails'>
      <Accordion
        title={ trustedPartnersLabel ? undefined : title?.text }
        subTitle={ trustedPartnersLabel ? undefined : subtitle }
        icon={ trustedPartnersLabel ? undefined : icon }
        variant='default'
        titleTag={ title?.htmlTag }
        expandAction={ expandAction }
        collapseAction={ collapseAction }
        setExpandMore={ setExpandMore }
        expandMore={ expandMore }
        headerChildren={ headerChildren }
      >
        <div className='OrderConfirmationDetails__expanded'>
          <Text textStyle={ 'label' }>{ orderId }</Text>

          <OrderDetailSection
            className='OrderConfirmationDetails__shipmentDetails'
            title={ shipmentTitle }
            titleHtmlTag={ tierLabelHtmlTag }
          >
            <Text textStyle='body'>{ shipmentDetail }</Text>
          </OrderDetailSection>

          { deliveryInformation && (
            <OrderDetailSection
              className='OrderConfirmationDetails__deliveryInformation'
              title={ deliveryInformation.title }
              titleHtmlTag={ tierLabelHtmlTag }
            >
              <Text htmlTag='p'
                textStyle='body-1'
                color={ 'validate-600' }
              >
                { deliveryInformation.date }
              </Text>
              <div className='OrderConfirmationDetails__deliveryInformation__markdown'>
                <Markdown
                  bodyStyle='body-2-noPadding'
                  htmlTag='p'
                  color='black'
                  fontWeight='bold'
                  cmsMarkdownContent={ deliveryInformation.helpText }
                />
              </div>
            </OrderDetailSection>
          ) }

          { pickupStoreInformation && (
            <>
              <OrderDetailSection
                className='OrderConfirmationDetails__alternatePerson'
                title={ pickupStoreInformation.alternatePersonTitle }
                titleHtmlTag={ tierLabelHtmlTag }
              >
                <Text textStyle='body'>{ pickupStoreInformation.alternatePerson }</Text>
              </OrderDetailSection>

              <OrderDetailSection
                className='OrderConfirmationDetails__pickupStoreInformation'
                title={ pickupStoreInformation.title }
                titleHtmlTag={ tierLabelHtmlTag }
              >
                <>
                  <div className='OrderConfirmationDetails__pickupStoreInformation__address'>
                    { pickupStoreInformation.storeInfo?.address1 &&
                      <Text
                        htmlTag='p'
                        textStyle='body-2'
                      >
                        { pickupStoreInformation.storeInfo.address1 }
                      </Text>
                    }
                    { pickupStoreInformation.storeInfo?.address2 &&
                      <Text
                        htmlTag='p'
                        textStyle='body-2'
                      >
                        { pickupStoreInformation.storeInfo.address2 }
                      </Text>
                    }
                    { pickupStoreInformation.storeInfo?.address3 &&
                      <Text
                        htmlTag='p'
                        textStyle='body-2'
                      >
                        { pickupStoreInformation.storeInfo.address3 }
                      </Text>
                    }
                  </div>
                  <div className='OrderConfirmationDetails__pickupStoreInformation__pickupAvailability'>
                    { showAvailabilityTag && (
                      <div className='OrderConfirmationDetails__pickupStoreInformation__pickupAvailability__tagContainer'>
                        { showAvailabilityTagAsATag && (
                          <Tag
                            { ...spreadProps( { ...pickupStoreInformation.availabilityTag } ) }
                          />
                        ) }
                        { showAvailabilityTagAsText && (
                          <div className='OrderConfirmationDetails__pickupStoreInformation__pickupAvailability__text' >
                            <Text
                              textStyle='body-sm'
                              color='neutral-500'
                            >
                              <Text
                                htmlTag='span'
                                fontWeight='bold'
                                textStyle='body-sm'
                                color='black'
                              >
                                { `${ pickupStoreInformation.availabilityTag.boldText } ` }
                              </Text>
                              { pickupStoreInformation.availabilityTag.message }
                            </Text>
                          </div>
                        ) }
                      </div>
                    ) }
                    <div className='OrderConfirmationDetails__pickupStoreInformation__curbsideLabel'>
                      <Text textStyle='body-sm'
                        color='neutral-600'
                      >
                        { pickupStoreInformation.curbsideLabel }
                      </Text>
                    </div>
                  </div>
                </>
              </OrderDetailSection>
            </>
          ) }

          { signatureInformation && (
            <OrderDetailSection
              className='OrderConfirmationDetails__signatureInformation'
              title={ signatureInformation.title }
              titleHtmlTag={ tierLabelHtmlTag }
            >
              <>
                <Text htmlTag='span'
                  textStyle='body-lg'
                  fontWeight='bold'
                >
                  { signatureInformation.requireLabel }
                </Text>
                <Text htmlTag='p'
                  textStyle='body'
                >
                  { signatureInformation.note }
                </Text>
              </>
            </OrderDetailSection>
          ) }

          { showInstructions && (
            <OrderDetailSection
              className='OrderConfirmationDetails__instructions'
              title={ instructionTitle }
              titleHtmlTag={ tierLabelHtmlTag }
            >
              <>
                { instructions && (
                  <Markdown
                    bodyStyle='body-2-noPadding'
                    bodyColor={ instructions.bodyColor }
                    cmsMarkdownContent={ instructions.body }
                  />
                ) }
                { showMap && (
                  <div className='OrderConfirmationDetails__instructions__map'>
                    <UltaAsset
                      imageUrl={ mapUrl }
                      altText={ pickupStoreInformation?.mapAltText?.replace( '%1', storeAddress ) }
                    />
                  </div>
                ) }
                { pickupStoreInformation?.helpText && (
                  <div className='OrderConfirmationDetails__pickupStoreInformation__storeHelpText'>
                    <Markdown
                      { ...spreadProps( { ...pickupStoreInformation.helpText } ) }
                      bodyStyle='body-2-noPadding'
                    />
                  </div>
                ) }
              </>
            </OrderDetailSection>
          ) }

          { ( hasProductItems || hasGifts ) && (
            <div className='OrderConfirmationDetails__items'>
              <OrderConfirmationItems
                giftItems={ giftItems }
                giftItemsTitle={ giftItemsTitle }
                giftItemsSubtitle={ giftItemsSubtitle }
                items={ items }
                itemsTitle={ itemsTitle }
                itemsSubtitle={ itemsSubtitle }
                listPriceAccessibilityLabel={ listPriceAccessibilityLabel }
                salePriceAccessibilityLabel={ salePriceAccessibilityLabel }
              />
            </div>
          ) }
        </div>
      </Accordion>
    </div>
  );
};

/**
  * Represents an OrderDetailSection component
  *
  * @method
  * OrderDetailSection - is used to return formatted sections for order confirmation
  * @param {string} props.children sets the children for section
  * @param {string} props.className sets the children for section
  * @param {string} props.title sets the title for the section
  * @param {string} props.titleHtmlTag sets the title html tag
  * @returns OrderDetailSection
*/
export const OrderDetailSection = ( props ) => {
  const {
    children,
    className,
    title,
    titleHtmlTag
  } = handleEmptyObjects( props );

  if( !children ){
    return null;
  }

  return (
    <section className={ classNames( 'OrderDetailSection', {
      [className]: className
    } ) }
    >
      { title && (
        <div className='OrderDetailSection__title'>
          <Text
            textStyle='eyebrow'
            htmlTag={ titleHtmlTag }
            color='neutral-500'
          >
            { title }
          </Text>
        </div>
      ) }
      <div className='OrderDetailSection__content'>
        { children }
      </div>
    </section>
  );
};

export const getMapUrl = ( data ) => {
  const {
    latitude,
    longitude,
    key,
    breakpoint
  } = handleEmptyObjects( data );

  if( !( latitude && longitude && key && breakpoint ) ){
    return;
  }

  return `${GOOGLE_STATIC_MAPS_URL}?markers=color:${MARKER_COLOR}|${latitude},${longitude}&zoom=${MAP_ZOOM_LEVEL_KEY}&size=${BOPIS_MAP_SIZE_SPLIT_CART[breakpoint?.CURRENT_BREAKPOINT]}&key=${key}&scale=2`;
};

/**
 * Property type definitions
 * @typedef OrderConfirmationDetailsProps
 * @type {object}
 * @property {object} collapseAction - sets the collapseAction for accordion
 * @property {object} deliveryInformation - sets the deliveryInformation
 * @property {object} pickupStoreInformation - sets the alternate pickup person details
 * @property {object} expandAction - sets the expandAction for accordion
 * @property {string} icon - sets the header icon for fulfillment type used for order
 * @property {object} instructions - sets the delivery instructions
 * @property {string} instructionTitle - sets the instruction title
 * @property {string} orderId - sets the order id for display
 * @property {string} shipmentDetail - sets the shipmentDetail
 * @property {string} shipmentTitle - sets the shipmentTitle
 * @property {object} signatureInformation - sets the signature related information
 * @property {string} subtitle - sets the subtitle for fulfillment type
 * @property {object} title - sets the title for fulfillment type
 * @property {object} giftItems - Sets gift items object
 * @property {object} giftItemsTitle - Gift with purchase title and htmlTag
 * @property {string} giftItemsSubtitle - Gift with purchase subtitle
 * @property {object} items - Sets product items object
 * @property {object} itemsTitle - set items title and htmlTag
 * @property {string} itemsSubtitle - set items subtitle
 * @property {string} listPriceAccessibilityLabel - accessiblity label for products listing price
 * @property {string} salePriceAccessibilityLabel - accessiblity label for products final price
 * @property {node} headerChildren - set items header children
 */


export const propTypes =  {
  icon:PropTypes.string,
  title:PropTypes.object,
  expandAction:PropTypes.object,
  orderId:PropTypes.string,
  shipmentTitle:PropTypes.string,
  shipmentDetail:PropTypes.string,
  pickupStoreInformation: PropTypes.shape( {
    title:PropTypes.string,
    alternatePersonTitle:PropTypes.string,
    alternatePerson:PropTypes.string,
    availabilityTag:PropTypes.object,
    curbsideLabel:PropTypes.string,
    storeInfo:PropTypes.object,
    helpText:PropTypes.object,
    mapAltText: PropTypes.string
  } ),
  collapseAction: PropTypes.object,
  deliveryInformation: PropTypes.shape( {
    date: PropTypes.string,
    helpText: PropTypes.string,
    title: PropTypes.string
  } ),
  instructions: PropTypes.shape( {
    alignment: PropTypes.string,
    body: PropTypes.string,
    bodyColor: PropTypes.string,
    bodyStyle: PropTypes.string
  } ),
  instructionTitle: PropTypes.string,
  signatureInformation: PropTypes.shape( {
    note: PropTypes.string,
    requireLabel: PropTypes.string,
    title: PropTypes.string
  } ),
  subtitle: PropTypes.string,
  giftItems: PropTypes.arrayOf(
    PropTypes.shape( {
      brandName: PropTypes.string,
      productName: PropTypes.string,
      variantTypeLabel: PropTypes.string,
      variantTypeDesc: PropTypes.string,
      quantityLabel: PropTypes.oneOfType( [PropTypes.string, PropTypes.number] ),
      image: PropTypes.object,
      finalPrice: PropTypes.string
    } )
  ),
  giftItemsTitle: PropTypes.object,
  giftItemsSubtitle: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape( {
      brandName: PropTypes.string,
      productName: PropTypes.string,
      variantTypeLabel: PropTypes.string,
      variantTypeDesc: PropTypes.string,
      quantityLabel: PropTypes.oneOfType( [PropTypes.string, PropTypes.number] ),
      image: PropTypes.object,
      finalPrice: PropTypes.string,
      deals: PropTypes.array,
      unitPriceBreakup: PropTypes.string
    } )
  ),
  itemsTitle: PropTypes.object,
  itemsSubtitle: PropTypes.string,
  listPriceAccessibilityLabel: PropTypes.string,
  salePriceAccessibilityLabel: PropTypes.string,
  headerChildren:PropTypes.node
};

OrderConfirmationDetails.propTypes = propTypes;

export default OrderConfirmationDetails;
